import { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Paper,
  InputAdornment,
  CircularProgress,
  Alert,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { axiosRequest } from "../../utility/axios.js";
import { apiBaseUrl } from "../../constant/endpoint.js";
import { useSelector } from "react-redux";
import { getSweetAlert, maskPhone } from "../../helper/index.js";

export default function CreateLoanDrawer({ open, onClose, refresh }) {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  const [phone, setPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [foundUser, setFoundUser] = useState(null);

  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    const input = phone.trim();
    if (!input) return;
    setSearching(true);
    setSearchError("");
    setFoundUser(null);
    setAmount("");
    setAmountError("");
    try {
      const param = /^\d{10}$/.test(input) ? `phone=${input}` : `playerId=${input}`;
      const res = await axiosRequest("GET", `${apiBaseUrl}/user_details?${param}`, token);
      const user = res?.data?.userDetails;
      if (user) setFoundUser(user);
      else setSearchError("No user found.");
    } catch {
      setSearchError("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const loanLimit = Number(foundUser?.loan_limit || 0);
  const currentLoanAmount = Number(foundUser?.loan_amount || 0);
  const availableLimit = Math.max(0, loanLimit - currentLoanAmount);
  const usagePercent = loanLimit > 0 ? (currentLoanAmount / loanLimit) * 100 : 0;

  const handleAmountChange = (val) => {
    setAmount(val);
    const num = Number(val);
    if (!val || num <= 0) {
      setAmountError("Enter a valid amount");
    } else if (num > availableLimit) {
      setAmountError(`Exceeds available limit of ${availableLimit.toLocaleString("en-IN")}`);
    } else {
      setAmountError("");
    }
  };

  const handleSubmit = async () => {
    if (!foundUser || !amount || !!amountError) return;
    setLoading(true);
    try {
      const res = await axiosRequest("POST", `${apiBaseUrl}/loan/create`, token, {
        playerId: foundUser.playerId,
        amount: Number(amount),
      });
      if (res?.data?.status) {
        getSweetAlert("success", res.data.message || "Loan created successfully");
        refresh();
        handleClose();
      } else {
        getSweetAlert("error", res?.data?.message || "Failed to create loan");
      }
    } catch (err) {
      console.error(err);
      getSweetAlert("error", "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhone("");
    setFoundUser(null);
    setSearchError("");
    setAmount("");
    setAmountError("");
    onClose();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: 460, p: 3, overflowY: "auto", height: "100%" }}>
        <Typography variant="h5" fontWeight={700}>Create New Loan</Typography>
        <Typography color="text.secondary" variant="body2" mb={2}>
          Search player by phone and enter the loan amount
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Phone Search */}
        <Typography fontWeight={600} mb={1}>Search Player</Typography>
        <Box display="flex" gap={1} mb={1}>
          <TextField
            fullWidth
            size="small"
            label="Phone / Player ID"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searching || !phone.trim()}
            sx={{ minWidth: 90, whiteSpace: "nowrap" }}
          >
            {searching ? <CircularProgress size={18} color="inherit" /> : "Search"}
          </Button>
        </Box>

        {searchError && <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>}

        {/* User Card */}
        {foundUser && (
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              background: "linear-gradient(135deg, #1e3c72, #2a5298)",
              color: "#fff",
            }}
          >
            <Typography fontWeight={700} variant="subtitle1" mb={1}>
              {foundUser.name || "—"}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Player ID</Typography>
                <Typography fontWeight={600} variant="body2">{foundUser.playerId || "—"}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Phone</Typography>
                <Typography fontWeight={600} variant="body2">{maskPhone(foundUser.phone) || "—"}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Ref Email</Typography>
                <Typography fontWeight={600} variant="body2" sx={{ fontSize: 11, wordBreak: "break-all" }}>
                  {foundUser.loan_ref_email || "Not set"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>Loan Limit</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ color: "#38ef7d" }}>
                  {loanLimit.toLocaleString("en-IN")}
                </Typography>
              </Grid>
            </Grid>

            {/* Loan usage bar */}
            {loanLimit > 0 && (
              <Box mt={2}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Outstanding: {currentLoanAmount.toLocaleString("en-IN")}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Available: {availableLimit.toLocaleString("en-IN")}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={usagePercent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "rgba(255,255,255,0.25)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: usagePercent > 80 ? "#ff5252" : "#38ef7d",
                    },
                  }}
                />
              </Box>
            )}
          </Paper>
        )}

        {/* Amount input */}
        {foundUser && (
          <>
            <Typography fontWeight={600} mb={1}>Loan Amount</Typography>
            <TextField
              fullWidth
              size="small"
              label="Amount ()"
              type="number"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              error={!!amountError}
              helperText={
                amountError ||
                (availableLimit > 0
                  ? `Available limit: ${availableLimit.toLocaleString("en-IN")}`
                  : "No available limit")
              }
              sx={{ mb: 3 }}
              inputProps={{ min: 1 }}
              disabled={availableLimit <= 0}
            />
          </>
        )}

        {/* Actions */}
        <Box display="flex" gap={2} mt={foundUser ? 0 : "auto"}>
          <Button fullWidth variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !foundUser || !amount || !!amountError || availableLimit <= 0}
          >
            {loading ? "Creating..." : "Create Loan"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
